import { useAuth0 } from '@auth0/auth0-react';
import {
  InputSelect,
  FullscreenLoading,
  SiteHeader,
  useModal,
  useMuiSwitchableTheme,
  SiteHeaderNavLink,
} from '@farmshare/ui-components';
import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Announcement,
  CalendarMonth,
  CalendarMonthOutlined,
  ChatBubbleOutline,
  ChecklistOutlined,
  GroupOutlined,
  LockOutline,
  Person,
  SettingsOutlined,
  WorkOutline,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { environment } from 'environments/environment';
import { find, first, map, some, sortBy } from 'lodash';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { setUser as sentrySetUser, getFeedback } from '@sentry/react';
import { ServiceWorkerProvider } from 'components/service-worker-provider';
import { isRunningInIframe } from 'utils/iframe-utils';

import {
  EnumUserRole,
  type User,
  type Vendor,
  useUserTenantVendorLazyQuery,
  useUserUpdateActiveVendorMutation,
} from 'lib/graphql';

import logo from './assets/images/logo-roca-green.svg';
import whiteLogo from './assets/images/logo-roca-white.svg';
import { userState, vendorState } from './state';
import { AuthProps } from '../../ui-components/src/components/auth/auth';

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
    LOQ: any[];
    luckyOrangeCompletedSetup: boolean;
  }
}

function tagUserForGTM(user?: User) {
  if (!user || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'user',
    userId: user._id,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    vendorId: user.active_vendor?._id,
    vendorName: user.active_vendor?.shop_name,
    role: user.role,
  });
}

function tagUserForSentry(user: User | null = null) {
  sentrySetUser(
    user
      ? {
          id: user._id,
          email: `${user.email}`,
          name: `${user.first_name} ${user.last_name}`,
          vendorId: user.active_vendor?._id,
          vendorName: user.active_vendor?.shop_name,
          role: user.role,
        }
      : null,
  );
}

let didSetupLuckyOrange = false;

function setupLuckyOrange(localUser?: User) {
  if (!didSetupLuckyOrange && localUser && import.meta.env.PROD) {
    didSetupLuckyOrange = true;
    window.LOQ = window.LOQ || [];
    window.luckyOrangeCompletedSetup = true;

    window.LOQ.push([
      'ready',
      async (LO: any) => {
        await LO.$internal.ready('visitor');
        LO.visitor.identify(localUser._id, {
          email: `${localUser.email}`,
          phone: `${localUser.phone}`,
          name: `${localUser.first_name} ${localUser.last_name}`,
          role: localUser.role,
        });
      },
    ]);
  }
}

export function App() {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    loginWithPopup,
    logout,
  } = useAuth0();

  const { save } = useModal();
  const { marginLeft, marginTop, themeMode } = useMuiSwitchableTheme();
  const [user, setUserState] = useRecoilState(userState);
  const [vendor, setVendor] = useRecoilState(vendorState);
  const [allVendors, setAllVendors] = useState<Vendor[]>([]);
  const [ready, setReady] = useState(false);
  const isIframe = isRunningInIframe();

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  const [search] = useSearchParams();

  const [getUser, getUserOperation] = useUserTenantVendorLazyQuery({
    notifyOnNetworkStatusChange: false,
  });
  const [updateUserActiveVendor] = useUserUpdateActiveVendorMutation();

  // Create iframe-aware login function
  const handleLogin = async () => {
    const authParams = { authorizationParams: { legacySameSiteCookie: false } };

    if (isIframe) {
      await loginWithPopup(authParams);
    } else {
      await loginWithRedirect({
        ...authParams,
        appState: { targetUrl: pathname },
      });
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      getUser();
    }
    setReady(true);
  }, [getUser, isAuthenticated]);

  useEffect(() => {
    if (getUserOperation.data?.userTenantVendor) {
      let localVendor: Vendor;
      const localUser = getUserOperation.data.userTenantVendor as User;

      if (getUserOperation.data.userTenantVendor.active_vendor) {
        localVendor = getUserOperation.data.userTenantVendor
          .active_vendor as Vendor;
      } else {
        localVendor = first(
          getUserOperation.data.userTenantVendor.tenant?.vendors,
        ) as Vendor;
      }

      if (localUser && localUser._id !== user?._id) {
        setUserState(localUser);
      }

      if (localVendor && localVendor._id !== vendor?._id) {
        setVendor(localVendor as Vendor);
      }
    }
  }, [
    getUserOperation.data?.userTenantVendor,
    setUserState,
    setVendor,
    vendor,
    user,
  ]);

  // Check if the user is loaded and matches the current user state
  // This is to ensure that the user data is fully loaded before proceeding
  const isUserLoaded = useMemo(() => {
    if (!ready || getUserOperation.loading) {
      return false;
    }
    return user?._id === getUserOperation.data?.userTenantVendor?._id;
  }, [
    ready,
    user,
    getUserOperation.loading,
    getUserOperation.data?.userTenantVendor,
  ]);

  useEffect(() => {
    if (search.has('error')) {
      // eslint-disable-next-line no-console
      console.error('[Auth Error]', search.get('error_description'));
      logout();
    }
  }, [logout, search]);

  const dashboardNavLinks = useMemo(() => {
    const _links: SiteHeaderNavLink[] = [];

    // Only non-processor users will see the scheduling link
    if (
      !some(
        getUserOperation.data?.userTenantVendor?.tenant?.modules,
        (m) => m.name === 'processor',
      )
    ) {
      if (user?.role === EnumUserRole.Basic) {
        _links.push(
          { label: 'Agenda', href: '/agenda', icon: <ChecklistOutlined /> },
          { label: 'Chat', href: '/chat', icon: <ChatBubbleOutline /> },
          {
            label: 'Jobs',
            icon: <WorkOutline />,
            dropDownItems: [
              { label: 'All Jobs', href: '/jobs' },
              { label: 'Schedule New Job', href: '/scheduling' },
            ],
          },
        );
      } else {
        _links.push({
          label: 'Scheduling',
          href: '/scheduling',
          icon: <CalendarMonthOutlined />,
        });
      }
    }

    if (getUserOperation.data?.userTenantVendor?.tenant?.modules) {
      _links.push(
        {
          label: 'Agenda',
          href: '/processor/agenda',
          icon: <ChecklistOutlined />,
        },
        {
          label: 'Calendar',
          href: '/processor/calendar',
          icon: <CalendarMonth />,
        },
        {
          label: 'Customers',
          icon: <GroupOutlined />,
          dropDownItems: [
            { label: 'All Customers', href: '/processor/customers' },
            { label: 'Chat', href: '/processor/chat' },
            { label: 'Waitlist', href: '/processor/settings/waitlist' },
          ],
        },
        {
          label: 'Jobs',
          icon: <WorkOutline />,
          dropDownItems: [
            { label: 'All Jobs', href: '/processor/jobs' },
            { label: 'Add Job', href: '/processor/add-job' },
            { label: 'Invoices', href: '/processor/invoices' },
          ],
        },
        {
          label: 'Settings',
          icon: <SettingsOutlined />,
          dropDownItems: [
            { label: 'Processor Settings', href: '/processor/settings' },
            { label: 'Capabilities', href: '/processor/capabilities' },
            { label: 'Cutsheets', href: '/processor/cutsheets' },
            { label: 'Integrations', href: '/integrations' },
            { label: 'Exports', href: '/exports' },
            { label: 'Notifications', href: '/processor/notifications' },
          ],
        },
      );
      const adminModule = find(
        getUserOperation.data.userTenantVendor.tenant.modules,
        (m) => m.label === 'Admin',
      );
      if (adminModule) {
        _links.push({
          label: adminModule.label,
          icon: <LockOutline />,
          href: adminModule.href,
        });
      }
    }

    _links.push({
      label: 'Support & News',
      icon: <Announcement />,
      dropDownItems: [
        {
          label: "What's New",
          href: 'https://farmshare.co/release-notes',
          external: true,
        },
        {
          label: 'Report Issue',
          onClick: () =>
            getFeedback()
              ?.createForm({
                colorScheme: themeMode === 'dark' ? 'dark' : 'light',
                useSentryUser: { email: 'email', name: 'name' },
                formTitle: 'Report an issue or send feedback',
                messagePlaceholder:
                  'Describe the issue or feedback, please include what page it occurred on, what you expected to happen, and what actually happened.',
                showBranding: false,
              })
              ?.then((form) => {
                form.appendToDom();
                form.open();
              }),
        },
      ],
    });

    return _links;
  }, [getUserOperation.data?.userTenantVendor, user]);

  if (
    allVendors.length === 0 &&
    (getUserOperation.data?.userTenantVendor?.tenant?.vendors?.length || 0) > 1
  ) {
    setAllVendors(
      getUserOperation.data?.userTenantVendor?.tenant?.vendors as Vendor[],
    );
  }

  const dropdownItems = useMemo(() => {
    const items: AuthProps['dropdownItems'] = [
      { label: 'Profile', icon: <Person />, href: '/profile' },
    ];

    if ((allVendors.length || 0) > 1) {
      items.push({
        label: 'Switch Vendor',
        icon: <FontAwesomeIcon icon={faRepeat} className="me-2" />,
        onClick: () => {
          save<{ vendor: string }>({
            type: 'save',
            title: 'Switch Vendor',
            icon: faRepeat,
            initialValues: { vendor: vendor?._id },
            saveIcon: faRepeat,
            saveText: 'Switch',
            body: (
              <InputSelect
                label="Vendor"
                options={map(
                  sortBy(allVendors, (v) => v.shop_name),
                  (v) => ({ label: v.shop_name, value: v._id }),
                )}
                floatingLabel
                required
              />
            ),
            onSubmit: (values) => {
              const vendor = find(allVendors, (v) => v._id === values.vendor);

              if (vendor) {
                updateUserActiveVendor({
                  variables: { userId: user._id, active_vendor: values.vendor },
                }).then(() => {
                  setVendor(vendor as Vendor);
                  window.location.reload();
                });
              }
            },
          });
        },
      });
    }

    // add on "key" prop for array rendering
    return items;
  }, [
    allVendors,
    save,
    setVendor,
    updateUserActiveVendor,
    user?._id,
    vendor?._id,
  ]);

  useEffect(() => {
    tagUserForGTM(user);
    setupLuckyOrange(user);
    tagUserForSentry(user);
  }, [user]);

  if (isLoading || isUserLoaded === false) {
    return <FullscreenLoading />;
  }

  return (
    <Suspense fallback={<FullscreenLoading />}>
      <ServiceWorkerProvider />
      <SiteHeader<User>
        user={user}
        isProduction={environment.production}
        env={import.meta.env.VITE_ENV}
        title="Partners"
        navLinks={dashboardNavLinks}
        loginFn={handleLogin}
        logoutFn={async () => {
          // In iframes, logout without redirect to avoid issues
          if (isIframe) {
            await logout();
          } else {
            await logout({
              logoutParams: { returnTo: window.location.origin },
            });
          }
        }}
        logo={
          <img
            src={!user ? whiteLogo : logo}
            height={39}
            alt={`farmshare ${!user && 'partners'} logo`}
            className="mt-1 text-body"
          />
        }
        dropdownItems={dropdownItems}
        vendorName={vendor?.shop_name}
      />
      <Box
        sx={{
          mx: 'auto',
          minHeight: `calc(100dvh - ${marginTop}px)`,
          width: '100%',
          pl: `${marginLeft}px !important`,
          transition: 'padding-left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
          pb: 2,
        }}
      >
        <Outlet />
      </Box>
    </Suspense>
  );
}
